import React, { useEffect, useState } from 'react'
import { adminApi } from '../services/api'
import { Loader } from '../components/UI'
import { formatToman, formatDateTimeFa, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const Pricing: React.FC = () => {
  const [, setSettings] = useState<any>(null)
  const [lastPrices, setLastPrices] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [refreshState, setRefreshState] = useState<string | null>(null)

  // فرم (مقادیر به تومان برای راحتی مدیر)
  const [mode, setMode] = useState<'auto' | 'manual'>('auto')
  const [goldToman, setGoldToman] = useState<number>(0)
  const [silverToman, setSilverToman] = useState<number>(0)
  const [buyback, setBuyback] = useState<number>(2)

  const load = async () => {
    setLoading(true)
    try {
      const res = await adminApi.getPricing()
      if (res.data.success) {
        const s = res.data.data.settings
        setSettings(s)
        setMode(s.mode)
        setGoldToman(s.goldRawPerGram / 10)
        setSilverToman(s.silverRawPerGram / 10)
        setBuyback(s.buybackPercent)
        setLastPrices(res.data.data.lastPrices)
      }
    } catch {
      toast.error('خطا در دریافت تنظیمات قیمت')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async (data: Record<string, any>) => {
    setRefreshState('save')
    try {
      const res = await adminApi.updatePricing(data)
      if (res.data.success) {
        toast.success('تنظیمات قیمت ذخیره شد')
        setSettings(res.data.data)
        setMode(res.data.data.mode)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در ذخیره')
    } finally {
      setRefreshState(null)
    }
  }

  const saveAll = () => {
    const data: Record<string, any> = { mode, buybackPercent: buyback }
    if (mode === 'manual') {
      data.goldRawPerGram = goldToman * 10
      data.silverRawPerGram = silverToman * 10
    }
    save(data)
  }

  const refreshPrices = async () => {
    setRefreshState('refresh')
    try {
      const res = await adminApi.refreshPrices()
      toast.success(`${toFaDigits(res.data.data?.updated || 0)} محصول به‌روزرسانی شد`)
      load()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در به‌روزرسانی قیمت')
    } finally {
      setRefreshState(null)
    }
  }

  if (loading) return <div className="py-20 flex justify-center"><Loader /></div>

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">قیمت خام طلا و نقره</h1>
          <p className="text-sm text-gray-500 mt-1">
            قیمت خام فلز، پایه محاسبه قیمت هر محصول است؛ درصد سود روی همین قیمت اعمال می‌شود.
          </p>
        </div>
        <button className="btn btn-primary" onClick={refreshPrices} disabled={refreshState === 'refresh'}>
          {refreshState === 'refresh' ? 'در حال به‌روزرسانی…' : 'به‌روزرسانی قیمت از منبع'}
        </button>
      </div>

      {lastPrices && (
        <div className="card p-5">
          <h2 className="font-black text-gray-900 mb-3">آخرین قیمت خام (منبع: {lastPrices.source})</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <span className="text-xs text-gray-500">طلای ۲۴ عیار</span>
              <div className="font-black text-lg">{formatToman(lastPrices.gold24KPerGram)}</div>
            </div>
            <div>
              <span className="text-xs text-gray-500">طلای ۱۸ عیار</span>
              <div className="font-black text-lg">{formatToman(lastPrices.gold18KPerGram)}</div>
            </div>
            <div>
              <span className="text-xs text-gray-500">نقره خام</span>
              <div className="font-black text-lg">{formatToman(lastPrices.silverRawPerGram)}</div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">آخرین به‌روزرسانی: {formatDateTimeFa(lastPrices.updatedAt)}</p>
        </div>
      )}

      <div className="card p-6 space-y-5">
        <div>
          <h2 className="font-black text-gray-900 mb-4">نحوه دریافت قیمت خام</h2>
          <div className="flex flex-col gap-3">
            <label className={`border-2 rounded-xl p-4 cursor-pointer ${mode === 'auto' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
              <input type="radio" checked={mode === 'auto'} onChange={() => setMode('auto')} className="ml-2" />
              <b>خودکار از API جهانی</b>
              <p className="text-xs text-gray-500 mt-1">
                GoldAPI (به ریال) یا Metals.live (دلار + نرخ دلار). هر {toFaDigits(15)} دقیقه به‌صورت خودکار به‌روز می‌شود.
              </p>
            </label>
            <label className={`border-2 rounded-xl p-4 cursor-pointer ${mode === 'manual' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
              <input type="radio" checked={mode === 'manual'} onChange={() => setMode('manual')} className="ml-2" />
              <b>دستی (توسط مدیر)</b>
              <p className="text-xs text-gray-500 mt-1">قیمت خام را خودتان بر اساس بازار داخلی وارد کنید.</p>
            </label>
          </div>
        </div>
        {mode === 'manual' && (
          <div className="grid sm:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
            <div>
              <label className="label">قیمت خام طلا ۲۴ عیار (تومان / هر گرم)</label>
              <input type="number" className="input" dir="ltr" value={goldToman} onChange={(e) => setGoldToman(parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <label className="label">قیمت خام نقره (تومان / هر گرم)</label>
              <input type="number" className="input" dir="ltr" value={silverToman} onChange={(e) => setSilverToman(parseFloat(e.target.value) || 0)} />
            </div>
          </div>
        )}

        <div className="border-t border-gray-100 pt-5">
          <label className="label">درصد کسر خرید از مشتری (خرید = قیمت خام − این درصد)</label>
          <input type="number" min={0} max={20} className="input" dir="ltr" value={buyback} onChange={(e) => setBuyback(parseFloat(e.target.value) || 0)} />
          <p className="text-xs text-gray-400 mt-1">مثلاً ۲ یعنی هنگام خرید طلا از مشتری، ۲٪ پایین‌تر از قیمت خام محاسبه می‌شود.</p>
        </div>

        <div className="flex justify-end border-t border-gray-100 pt-4">
          <button className="btn btn-primary !px-8" onClick={saveAll} disabled={refreshState === 'save'}>
            {refreshState === 'save' ? 'در حال ذخیره…' : 'ذخیره تنظیمات قیمت'}
          </button>
        </div>
      </div>

      <div className="card p-5 text-xs text-gray-500 leading-6">
        قیمت‌های این صفحه بر حسب «تومان» نمایش داده می‌شوند؛ داخل سیستم به ریال ذخیره و محاسبه می‌شود.
      </div>
    </div>
  )
}

export default Pricing
